import { RadioGroup } from '@mui/material';
import type { ReactNode } from 'react';
import AllPermissionsTable from './AllPermissionsTable';
import { FormGroup, LabeledTextInput, LabeledRadio } from '../../../generic/Forms';
import Text from '../../../generic/Text';
import type { EditingRoleState } from '../../../../redux/roles';
import type { RoleScope } from '../../../../../typings/enums';
import Button, { ButtonGroup } from '../../../generic/Button';
import type { AllPermissionsItem } from './hooks/useAllPermissions';
import type { RolePermissions } from '../../../../../shared/model-typings';

type AddEditRoleSectionProps = {
  heading: string;
  subheading?: string;
  children: ReactNode;
};

/**
 * AddEditRoleSection represents one of the three "sections" of the page.
 *
 * @param {AddEditRoleSectionProps} props - The props for the AddEditRoleSection.
 * @param {string} props.heading - H2 heading for the section.
 * @param {string} props.subheading - Optional paragraph text for under the heading.
 * @param {ReactNode} props.children - Section contents.
 */
const AddEditRoleSection = ({ heading, subheading, children }: AddEditRoleSectionProps) => (
  <section className="roles__add-edit-role__section">
    <Text.H2>{heading}</Text.H2>
    {subheading && <Text.Paragraph>{subheading}</Text.Paragraph>}
    {children}
  </section>
);

type AddEditRoleProps = {
  name: string;
  description: string;
  scope: RoleScope;
  permissions: RolePermissions;
  isAddMode: boolean;
  isReadOnly: boolean;
  isSaving: boolean;
  updateEditingRole: (updatedEditingRole: Partial<EditingRoleState>) => void;
  setEditingPermissions: (permissions: AllPermissionsItem[]) => void;
  onCancelClick: () => void;
  onSaveClick: () => void;
};

/**
 * Display ("dumb") view for displaying the Add/Edit Roles page.
 *
 * @param {AddEditRoleProps} props - The props for the AddEditRole.
 * @param {string} props.name - The name of the role being added (default: "Add Role")
 * @param {string} props.description - Description for this role.
 * @param {RoleScope} props.scope - Scope of the role.
 * @param {RolePermissions} props.permissions - List of active permissions
 * @param {boolean} props.isAddMode - Are we editing an existing role or adding a new one?
 * @param {boolean} props.isReadOnly - If we are looking at an existing built-in (non-custom) role?
 * @param {boolean} props.isSaving - If the role is currently being saved.
 * @param {(updatedEditingRole: Partial<EditingRoleState>) => void} props.updateEditingRole - Function to update the state in Redux, which is what actually gets saved to the DB.
 * @param {(permissions: EditingRoleState['permissions']) => void} props.setEditingPermissions - Function to properly set the permissions in Redux when the AllPermissionsTable mounts.
 * @param {() => void} props.onCancelClick - Function which navigates the user back to the Roles index and resets the Redux state.
 * @param {() => void} props.onSaveClick - Function for saving the add/edit role state.
 */
const AddEditRole = ({
  name,
  description,
  scope,
  permissions,
  isAddMode,
  isReadOnly,
  isSaving,
  updateEditingRole,
  setEditingPermissions,
  onCancelClick,
  onSaveClick,
}: AddEditRoleProps) => (
  <form className="roles__add-edit-role">
    <header className="roles__add-edit-role__header">
      <Text.H1>{isAddMode ? 'Add Role' : `Editing ${name} Role`}</Text.H1>
    </header>
    <AddEditRoleSection heading="Details">
      <FormGroup spacing="lg">
        <LabeledTextInput
          label="Role name"
          value={name}
          placeholder="Role name"
          onChange={(updatedName: string) => !isReadOnly && updateEditingRole({ name: updatedName })}
          isDisabled={isReadOnly}
          isRequired
        />
        <LabeledTextInput
          label="Role description"
          value={description}
          placeholder="Role description"
          onChange={(updatedDescription: string) =>
            !isReadOnly && updateEditingRole({ description: updatedDescription })}
          isDisabled={isReadOnly}
          isRequired
        />
      </FormGroup>
    </AddEditRoleSection>
    <hr className="roles__add-edit-role__divider" />
    <AddEditRoleSection heading="Type" subheading="Role will be for:">
      <RadioGroup
        value={scope}
        onChange={(_event, updatedScope: RoleScope) =>
          //  Check for add mode, because you cannot change the scope of a role once it's been created.
          isAddMode && !isReadOnly && updateEditingRole({ scope: updatedScope })}
      >
        <FormGroup spacing="xs">
          <LabeledRadio label="Organization" value="organization" isDisabled={!isAddMode || isReadOnly} />
          <LabeledRadio label="Team" value="team" isDisabled={!isAddMode || isReadOnly} />
        </FormGroup>
      </RadioGroup>
    </AddEditRoleSection>
    <hr className="roles__add-edit-role__divider" />
    <AddEditRoleSection heading="Permissions" subheading="Inside the organization or team">
      <AllPermissionsTable
        isReadOnly={isReadOnly}
        scope={scope}
        rolePermissions={permissions}
        updatePermissions={(updatedPermissions) =>
          !isReadOnly && updateEditingRole({ permissions: updatedPermissions })}
        setEditingPermissions={(updatedPermissions) => !isReadOnly && setEditingPermissions(updatedPermissions)}
      />
    </AddEditRoleSection>
    <footer className="roles__add-edit-role__footer">
      <ButtonGroup>
        <Button size="medium" type="secondary" onClick={onCancelClick}>
          Cancel
        </Button>
        {!isReadOnly && (
          <Button size="medium" type="primary" onClick={onSaveClick} isDisabled={isSaving || !(name && description)}>
            {isSaving ? 'Saving' : 'Save'}
          </Button>
        )}
      </ButtonGroup>
    </footer>
  </form>
);

export default AddEditRole;
